import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";

const isImage = (mime, name) =>
  (mime || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name || "");

const isPdf = (mime, name) =>
  (mime || "").includes("pdf") || /\.pdf$/i.test(name || "");

export default function EvidenceViewerModal({
  isOpen,
  onClose,
  evidenceFiles = [],

  selectedFile,
  onSelectFile,

  // Instead of FileViewer: caller provides a function that returns Blob
  // signature: async (file) => Blob
  fetchPreviewBlob,

  onDownload, // optional: (file) => void
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewMime, setPreviewMime] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const supported = useMemo(() => {
    if (!selectedFile) return false;
    const mime = selectedFile?.mimeType || selectedFile?.type || "";
    const name = selectedFile?.name || "";
    return isImage(mime, name) || isPdf(mime, name);
  }, [selectedFile]);

  // Build preview URL whenever file changes / modal opens
  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setErr("");
      setPreviewMime("");
      if (!isOpen || !selectedFile) {
        setPreviewUrl("");
        return;
      }

      // revoke old url
      if (previewUrl) URL.revokeObjectURL(previewUrl);

      // block unsupported
      if (!supported) {
        setPreviewUrl("");
        return;
      }

      try {
        setLoading(true);
        const blob = await fetchPreviewBlob(selectedFile); // must return Blob
        if (!mounted) return;

        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPreviewMime(blob.type || "");
      } catch (e) {
        if (!mounted) return;
        setErr("Failed to load preview. Please try again.");
        setPreviewUrl("");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedFile, supported]);

  const close = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setErr("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <div style={{ width: "100%", height: "100%" }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="m-0">Console Evidence</h5>

          <div className="d-flex gap-2">
            {selectedFile && onDownload && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => onDownload(selectedFile)}
              >
                Download
              </button>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={close}
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="row g-2" style={{ minHeight: 520 }}>
          {/* Left panel */}
          <div className="col-4">
            <select
              className="form-select"
              value={selectedFile?.name || ""}
              onChange={(e) =>
                onSelectFile(
                  evidenceFiles.find((f) => f.name === e.target.value) || null
                )
              }
              disabled={!evidenceFiles.length}
            >
              {evidenceFiles.map((f) => (
                <option key={f.fileId} value={f.name}>
                  {f.name}
                </option>
              ))}
            </select>

            <div className="small text-muted mt-2">
              Accepted types: <span className="fw-semibold">Images, PDF</span>
            </div>

            <div className="small text-muted mt-2">
              {evidenceFiles.length} file(s)
            </div>

            {err && (
              <div className="alert alert-danger py-2 mt-2 mb-0">
                {err}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className="col-8">
            {!selectedFile ? (
              <div className="border rounded p-4 text-muted">
                No evidence file selected.
              </div>
            ) : !supported ? (
              <div className="border rounded p-4">
                <div className="fw-semibold mb-1">Unsupported preview</div>
                <div className="text-muted">
                  This viewer supports <b>images</b> and <b>PDF</b> only. Please download to view.
                </div>
              </div>
            ) : loading ? (
              <div className="border rounded p-4 text-muted">Loading preview…</div>
            ) : previewUrl ? (
              isPdf(previewMime, selectedFile.name) ? (
                <iframe
                  title="pdf-preview"
                  src={previewUrl}
                  style={{ width: "100%", height: 520, border: "1px solid #ddd", borderRadius: 8 }}
                />
              ) : (
                <div className="border rounded p-2" style={{ height: 520, overflow: "auto" }}>
                  <img
                    src={previewUrl}
                    alt={selectedFile.name}
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>
              )
            ) : (
              <div className="border rounded p-4 text-muted">
                No preview available.
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
